import { CARK_TEMA } from "./cark-tema";

/**
 * ÇARK GEOMETRİSİ — saf (yan etkisiz) hesaplar. "Klasik TV" çiziminin (`cark-klasik-tv.dc.html`) `geo()`, `yay()`,
 * lamba ve konfeti üretimi SAYI SAYI buraya taşındı; ekran yalnız bunları çizer.
 *
 * SONUCU SUNUCU BELİRLER (Emre 07.10.2026, dalga-2-tasarim.md §10 "ÇARK ORANLARI"): burada ağırlık, olasılık ya da
 * sonuç seçimi YOK. `Math.random` (ya da testte tohumlu üreteç) yalnız iki şey için kullanılır:
 *   1. sunucunun döndürdüğü puanı taşıyan dilimlerden HANGİSİNDE ve o dilimin NERESİNDE durulacağı;
 *   2. konfeti parçalarının görünümü.
 * İkisi de sonucu değiştirmez: ok her koşulda sunucunun puanını taşıyan dilimin İÇİNDE durur.
 */

/** Tasarımdaki dilim dizisi (saat yönünde, ilk dilimin ortası tam üstte). */
export const TASARIM_DIZISI: readonly number[] = [
  5, 20, 10, 50, 5, 500, 10, 20, 5, 1000, 10, 20, 5, 5000, 10, 50,
];

/** Tasarımın `boyOrani`: büyük puan daha dar dilim. */
const TASARIM_BOY_ORANI: Readonly<Record<number, number>> = {
  5: 26,
  10: 23,
  20: 21,
  50: 19,
  500: 17,
  1000: 15,
  5000: 12,
};

/** Yedek düzende sıra → boy (tasarım tablosunun küçükten büyüğe sırası). Sekizinci ve sonrası en dar boyda kalır. */
const BOY_SIRASI: readonly number[] = [26, 23, 21, 19, 17, 15, 12];

/** Yedek düzende en az dilim sayısı (tasarımla aynı yoğunluk). */
const EN_AZ_DILIM = 16;

/** Tasarım: hedef açıya `360 * 7` eklenir — her çevirmede sabit yedi tam tur (sonuca göre değişmez). */
export const TUR_SAYISI = 7;

/** Tasarım: ok dilim aralığının %30–%70'inde durur — kenara yanaşma ("kıl payı") yok. */
export const DURUS_ALT = 0.3;
export const DURUS_GENISLIK = 0.4;

export interface Dilim {
  puan: number;
  yol: string;
  renk: string;
}

export interface Etiket {
  /** Ait olduğu dilimin sırası (erişilebilirlik değil, test ve çizim anahtarı için). */
  dilim: number;
  x: string;
  y: string;
  boy: number;
  renk: string;
  kontur: string;
  metin: string;
  don: string;
}

export interface Nokta {
  x: string;
  y: string;
}

export interface CarkDuzeni {
  /** Tasarımın kendi dizisi mi, sunucunun puanlarından kurulan yedek mi. */
  tasarim: boolean;
  dizi: readonly number[];
  dilimler: Dilim[];
  etiketler: Etiket[];
  mandallar: Nokta[];
  /** Her dilimin [başlangıç, bitiş] açısı (derece, saat yönü, 0 = üst). İlki negatif başlar. */
  araliklar: [number, number][];
}

const K = Math.PI / 180;

/** Tasarımın `yay()`ı: merkezden r yarıçaplı, b→e derece arası dilim yolu (iki ondalık). */
export function yay(r: number, b: number, e: number): string {
  return (
    "M0 0 L" +
    (r * Math.sin(b * K)).toFixed(2) +
    " " +
    (-r * Math.cos(b * K)).toFixed(2) +
    " A" +
    r +
    " " +
    r +
    " 0 0 1 " +
    (r * Math.sin(e * K)).toFixed(2) +
    " " +
    (-r * Math.cos(e * K)).toFixed(2) +
    " Z"
  );
}

/** Tasarımın `geo()`ı, dizi ve boy kuralı dışarıdan verilerek. */
function geometri(
  dizi: readonly number[],
  boyOrani: (p: number) => number,
  tasarim: boolean,
): CarkDuzeni {
  const t = CARK_TEMA;
  const olcek = 360 / dizi.reduce((s, p) => s + boyOrani(p), 0);
  let bas = (-boyOrani(dizi[0]!) * olcek) / 2;
  let ci = 0;
  const dilimler: Dilim[] = [];
  const etiketler: Etiket[] = [];
  const mandallar: Nokta[] = [];
  const araliklar: [number, number][] = [];
  dizi.forEach((p, sira) => {
    const aci = boyOrani(p) * olcek;
    const e = bas + aci;
    const m = bas + aci / 2;
    const renk = t.ozel[p] ?? t.renkler[ci++ % t.renkler.length]!;
    dilimler.push({ puan: p, yol: yay(176, bas, e), renk });
    // Rakamlar ALT ALTA: her hane aynı açıda, dıştan içe adım adım (tasarım: 4+ hane 25, diğerleri 30 birim).
    const rakamlar = String(p).split("");
    const boy = rakamlar.length >= 4 ? 25 : 30;
    const adim = boy * 0.86;
    const bas0 = 152 - (4 - rakamlar.length) * adim * 0.25;
    rakamlar.forEach((c, j) => {
      const rr = bas0 - j * adim;
      const x = (rr * Math.sin(m * K)).toFixed(1);
      const y = (-rr * Math.cos(m * K)).toFixed(1);
      etiketler.push({
        dilim: sira,
        x,
        y,
        boy,
        renk: t.ozelYazi[p] ?? t.normalYazi,
        kontur: t.ozelKontur[p] ?? t.kontur,
        metin: c,
        don: "rotate(" + m.toFixed(1) + " " + x + " " + y + ")",
      });
    });
    mandallar.push({
      x: (170 * Math.sin(bas * K)).toFixed(1),
      y: (-170 * Math.cos(bas * K)).toFixed(1),
    });
    araliklar.push([bas, e]);
    bas = e;
  });
  return { tasarim, dizi, dilimler, etiketler, mandallar, araliklar };
}

function tekilSirali(puanlar: readonly number[]): number[] {
  return [...new Set(puanlar.filter((p) => Number.isFinite(p)))].sort((a, b) => a - b);
}

const TASARIM_KUMESI = tekilSirali(TASARIM_DIZISI);

/**
 * YEDEK DÜZEN — sunucunun puan tablosu tasarımdakinden farklıysa (gelecekte tablo değişirse).
 *
 * Çark yalnız sunucunun gerçekten verebileceği puanları gösterir: tasarımın 5000'i sunucuda yoksa çarkta da yok,
 * sunucuda yeni bir puan varsa çarkta da var. Kural tasarımla aynı: küçük puan geniş ve çok dilim, büyük puan dar
 * ve tek dilim. En az 16 dilim; artan dilimler en küçük puanların yarısına sırayla dağıtılır. Dizilişte aynı puan
 * yan yana gelmesin diye her adımda en çok kalan (ve öncekiyle aynı olmayan) puan seçilir — deterministik.
 */
function yedekDizi(puanlar: number[]): { dizi: number[]; boy: Map<number, number> } {
  const boy = new Map<number, number>();
  puanlar.forEach((p, i) => boy.set(p, BOY_SIRASI[Math.min(i, BOY_SIRASI.length - 1)]!));
  const toplam = Math.max(EN_AZ_DILIM, puanlar.length);
  const adet = new Map<number, number>(puanlar.map((p) => [p, 1]));
  const artiAlan = Math.max(1, Math.ceil(puanlar.length / 2));
  for (let k = 0; k < toplam - puanlar.length; k++) {
    const p = puanlar[k % artiAlan]!;
    adet.set(p, adet.get(p)! + 1);
  }
  const dizi: number[] = [];
  for (let n = 0; n < toplam; n++) {
    const onceki = dizi[dizi.length - 1];
    const ilk = dizi[0];
    const sonAdim = n === toplam - 1;
    let secilen: number | undefined;
    for (const p of puanlar) {
      const kalan = adet.get(p)!;
      if (kalan === 0) continue;
      if (p === onceki && puanlar.length > 1) continue;
      if (sonAdim && p === ilk && puanlar.length > 2) continue;
      if (secilen === undefined || kalan > adet.get(secilen)!) secilen = p;
    }
    // Kısıtların hepsi sağlanamıyorsa (çok az farklı puan) kalan herhangi bir puan alınır: sonuç yine doğru.
    if (secilen === undefined) secilen = puanlar.find((p) => adet.get(p)! > 0)!;
    adet.set(secilen, adet.get(secilen)! - 1);
    dizi.push(secilen);
  }
  return { dizi, boy };
}

const ONBELLEK = new Map<string, CarkDuzeni>();

/**
 * Puan kümesine göre çark düzeni. Aynı küme için HEP AYNI nesne döner (modül düzeyi önbellek): çark SVG'si
 * `React.memo` ile yalnız düzen değişince yeniden çizilir; kutlama/sonuç durumu değişince çizilmez.
 *
 * Küme tasarımınkiyle aynıysa (ya da sunucu hiç puan göndermediyse) tasarımın kendi dizisi; değilse yedek düzen.
 */
export function duzenGetir(puanlar: readonly number[]): CarkDuzeni {
  const kume = tekilSirali(puanlar);
  const tasarimMi =
    kume.length === 0 ||
    (kume.length === TASARIM_KUMESI.length && kume.every((p, i) => p === TASARIM_KUMESI[i]));
  const anahtar = tasarimMi ? "tasarim" : kume.join(",");
  const kayitli = ONBELLEK.get(anahtar);
  if (kayitli) return kayitli;
  let duzen: CarkDuzeni;
  if (tasarimMi) {
    duzen = geometri(TASARIM_DIZISI, (p) => TASARIM_BOY_ORANI[p]!, true);
  } else {
    const { dizi, boy } = yedekDizi(kume);
    duzen = geometri(dizi, (p) => boy.get(p)!, false);
  }
  ONBELLEK.set(anahtar, duzen);
  return duzen;
}

/** Düzende bu puanı taşıyan dilim var mı. */
export function puanVarMi(duzen: CarkDuzeni, puan: number): boolean {
  return duzen.dizi.includes(puan);
}

/**
 * Hedef açı (tasarımın `cevir()` hesabı): sunucunun puanını taşıyan dilimlerden biri ve o dilimin %30–%70'i arasında
 * bir nokta seçilir; çark ÖNCEKİ açıdan ileri doğru `tur` tam tur + noktanın oka gelmesi için gereken açı kadar döner.
 * Geri sarma yok: sonuç her zaman öncekinden büyüktür (tur > 0 iken).
 *
 * `tur = 0`: animasyonsuz konumlama (açılışta `sonCevirme` gösterilirken çark o puanın üstünde dursun diye).
 */
export function hedefAciHesapla(
  onceki: number,
  duzen: CarkDuzeni,
  puan: number,
  rastgele: () => number,
  tur: number = TUR_SAYISI,
): { aci: number; dilim: number } {
  const adaylar: number[] = [];
  duzen.dizi.forEach((p, j) => {
    if (p === puan) adaylar.push(j);
  });
  if (adaylar.length === 0) {
    // Çağıran bunu önler (`duzenGetir` sonucu içeren kümeyle çağrılır); yine de sessizce yanlış dilimde durmasın.
    throw new Error(`Çark düzeninde ${puan} puanlı dilim yok.`);
  }
  const dilim = adaylar[Math.min(adaylar.length - 1, Math.floor(rastgele() * adaylar.length))]!;
  const [b, e] = duzen.araliklar[dilim]!;
  const nokta = b + (e - b) * (DURUS_ALT + rastgele() * DURUS_GENISLIK);
  const taban = onceki - (onceki % 360);
  return { aci: taban + 360 * tur + ((((360 - nokta) % 360) + 360) % 360), dilim };
}

/**
 * Okun altındaki dilim (tersine hesap). Çark `aci` derece saat yönünde döndüğünde üstteki ok, çarkın kendi
 * koordinatında `-aci` açısını gösterir.
 */
export function okAltindaki(
  aci: number,
  duzen: CarkDuzeni,
): { dilim: number; puan: number; oran: number } {
  const bas0 = duzen.araliklar[0]![0];
  let a = ((-aci % 360) + 360) % 360;
  if (a >= bas0 + 360) a -= 360;
  if (a < bas0) a += 360;
  for (let i = 0; i < duzen.araliklar.length; i++) {
    const [b, e] = duzen.araliklar[i]!;
    if (a >= b && a < e) return { dilim: i, puan: duzen.dizi[i]!, oran: (a - b) / (e - b) };
  }
  // Kayan nokta yuvarlaması son dilimin bitişine denk getirirse.
  const son = duzen.araliklar.length - 1;
  return { dilim: son, puan: duzen.dizi[son]!, oran: 1 };
}

/** Çerçevedeki 32 ampulün yeri (tasarım: r=189, 11,25° arayla, ilki tam üstte). */
export const LAMBALAR: readonly Nokta[] = Array.from({ length: 32 }, (_, i) => {
  const a = i * 11.25 * K;
  return { x: (189 * Math.sin(a)).toFixed(1), y: (-189 * Math.cos(a)).toFixed(1) };
});

export interface KonfetiParcasi {
  sol: string;
  en: string;
  boy: string;
  renk: string;
  yuvarlak: string;
  sure: string;
  gecikme: string;
}

/** Tasarım: puan ≥1000 → 110 parça, ≥50 → 80, diğerleri 60. */
export function konfetiAdedi(puan: number): number {
  return puan >= 1000 ? 110 : puan >= 50 ? 80 : 60;
}

/** Tasarımın konfeti üretimi (renkler: dilim renkleri + vurgu + beyaz; süre 2,2–4 sn; gecikme 0–0,9 sn). */
export function konfetiUret(puan: number, rastgele: () => number): KonfetiParcasi[] {
  const renkler: string[] = [...CARK_TEMA.renkler, CARK_TEMA.vurgu, CARK_TEMA.parlakBeyaz];
  return Array.from({ length: konfetiAdedi(puan) }, () => {
    const en = 6 + rastgele() * 7;
    return {
      sol: (rastgele() * 100).toFixed(1) + "%",
      en: en.toFixed(1) + "px",
      boy: (rastgele() < 0.5 ? en : en * 1.8).toFixed(1) + "px",
      renk: renkler[Math.floor(rastgele() * renkler.length)]!,
      yuvarlak: rastgele() < 0.3 ? "50%" : "2px",
      sure: (2.2 + rastgele() * 1.8).toFixed(2) + "s",
      gecikme: (rastgele() * 0.9).toFixed(2) + "s",
    };
  });
}
